import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Link /* , Font */
} from '@react-pdf/renderer';
import MarkdownIt from 'markdown-it';
import PropTypes from 'prop-types';
import { Html } from 'react-pdf-html';

import { BUSINESS_COST_SECTIONS } from '#client/components/utils/constants';
import * as ROUTES from '#client/constants/routes';

const CLOUDINARY_BASE_URL = 'https://res.cloudinary.com/zenbusiness/q_auto/v1/shared-assets/s2';
const ZENBUSINESS_LOGO_URL =
  'https://res.cloudinary.com/zenbusiness/image/upload/v1749845000/shared-assets/s2/logo/zb-velo-compact-color.png';
const ICON_BUSINESS_IDEAS_OVERVIEW_URL = `${CLOUDINARY_BASE_URL}/raster/`;

// Markdown parser (keep HTML disabled for safety; add plugins for GFM-like features)

const md = new MarkdownIt({
  html: false,
  breaks: true, // respect single line breaks
  linkify: true // auto-link URLs
});

const FOOTER_HEIGHT = 40;

const styles = StyleSheet.create({
  page: {
    padding: 32,
    paddingBottom: 32 + FOOTER_HEIGHT,
    fontSize: 12,
    lineHeight: 1.5,
    color: '#111'
  },
  header: {
    marginBottom: 24,
    borderBottom: '1pt solid #ccc',
    paddingBottom: 12
  },
  subheader: {
    fontSize: 10,
    color: 'grey',
    textTransform: 'uppercase',
    marginBottom: 24
  },
  title: { fontSize: 20, fontWeight: 'bold', marginBottom: 12 },
  subtitle: { fontSize: 12, color: '#444', marginBottom: 12 },

  /** START USER INPUT STYLES */
  userInput: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 12
  },
  userInputItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'left'
  },
  userInputLabel: {
    color: '#666',
    marginRight: 12,
    width: '30%'
  },
  userInputValue: {
    whiteSpace: 'pre-wrap',
    width: '70%'
  },
  /** END USER INPUT STYLES */

  /** START SECTION STYLES */
  summarySection: {
    backgroundColor: '#E1EBFF',
    borderRadius: 8,
    padding: '20px 24px 12px 24px', // padding bottom + 8px (<p> margin bottom)
    marginBottom: 16
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6
  },
  summaryText: {
    fontSize: 11,
    color: '#666'
  },

  section: {
    marginBottom: 16,
    borderBottom: '1pt solid #eee',
    borderRadius: 4
  },
  // sectionWhy: {
  //   backgroundColor: '#EAF2FF', // light blue
  //   borderRadius: 8,
  //   padding: '20px 24px 12px 24px', // padding bottom + 8px (<p> margin bottom)
  //   borderBottom: 0,
  //   marginBottom: 16
  //   // remove divider under this card
  // },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 0
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#222'
  },
  costRange: {
    fontSize: 14,
    fontWeight: 'medium',
    color: '#111',
    whiteSpace: 'pre-wrap'
  },
  sectionContent: {
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    marginBottom: 8
  },
  sectionContentBubble: {
    fontSize: 11,
    whiteSpace: 'pre-wrap',
    paddingLeft: 0,
    color: '#666',
    lineHeight: 1.6
  },
  sectionIcon: {
    width: 20,
    height: 20,
    marginRight: 6
  },
  sectionIconTotal: {
    width: 32,
    height: 32,
    marginRight: 0,
    backgroundColor: '#1976D2',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  disclaimer: {
    fontSize: 10,
    fontStyle: 'italic',
    marginTop: 4,
    paddingLeft: 44
  },
  footerImage: {
    width: '100%'
    // height: 60,
    // marginRight: 6
  },
  /** END SECTION STYLES */
  footerImageIcon: {
    width: 180,
    height: 12
    // marginRight: 6
  },
  footer: {
    position: 'absolute',
    left: 32,
    right: 32,
    bottom: 24, // a little inset from page edge; change to 32 to align with padding
    height: FOOTER_HEIGHT,
    backgroundColor: '#fff' // keeps it clean if your viewer has a dark chrome
  },
  footerRule: {
    borderTop: '1pt solid #ddd',
    marginBottom: 6
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  footerLeft: {
    fontSize: 10,
    color: '#666'
  },
  footerRightImg: {
    width: 180, // your powered-by image width
    height: 15 // match the image’s aspect
  },
  // Optional: minimal CSS map for Html component (applies to nested HTML tags)
  htmlStyles: {
    // You can pass this object into <Html stylesheet={...} />
    'h1, h2, h3': { marginTop: 8, marginBottom: 6, fontWeight: 'bold' },
    h1: { fontSize: 18 },
    h2: { fontSize: 16 },
    h3: { fontSize: 14 },
    p: { marginBottom: 8 },
    ul: { marginBottom: 8, paddingLeft: 4, listStyleType: 'disc' },
    ol: { marginBottom: 8, paddingLeft: 4, listStyleType: 'disc' },
    li: { marginBottom: 4, listStyleType: 'disc' },
    a: { textDecoration: 'underline' },
    code: { fontSize: 11 },
    pre: {
      fontSize: 11,
      padding: 6,
      border: '1pt solid #ddd',
      borderRadius: 4,
      marginBottom: 8
    },
    blockquote: {
      borderLeft: '2pt solid #ccc',
      paddingLeft: 8,
      color: '#666',
      fontStyle: 'italic',
      marginBottom: 8
    },
    table: {
      width: '100%',
      border: '1pt solid #ddd',
      borderCollapse: 'collapse',
      marginBottom: 10
    },
    th: { padding: 4, fontWeight: 'bold', borderBottom: '1pt solid #ddd' },
    td: { padding: 4, borderBottom: '1pt solid #eee' },
    hr: { borderBottom: '1pt solid #ccc', marginTop: 8, marginBottom: 8 }
  }
});

// eslint-disable-next-line react/prop-types
function MarkdownPDFSection({ markdown }) {
  if (!markdown) return null;
  const html = md.render(markdown);
  return (
    <Html style={styles.sectionContent} stylesheet={styles.htmlStyles}>
      {html}
    </Html>
  );
}

// Helper function to format section titles from camelCase or snake_case keys
const formatSectionTitle = (key) => {
  return key
    .replace(/_/g, ' ') // Replace underscores with spaces (for snake_case)
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters (for camelCase)
    .replace(/&/g, 'and') // Replace & with 'and'
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

const getCostRangeStyle = (cost) => {
  return cost?.length > 45 ? { ...styles.costRange, fontSize: 12 } : styles.costRange;
};

const DISCLAIMER = '*Disclaimer: These are only estimates. Actual costs may vary.*';

export function BusinessResultPDF({ environment, report }) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image src={ZENBUSINESS_LOGO_URL} style={{ width: 80, marginBottom: 20 }} />

        <View style={styles.header}>
          <Text style={styles.subheader}>Detailed business report</Text>
          <Text style={styles.title}>{report.title}</Text>
          <Text style={styles.subtitle}>{report.description}</Text>
          {report.userInput && (
            <View style={styles.userInput}>
              {report.userInput.businessType && (
                <View style={styles.userInputItem}>
                  <Text style={styles.userInputLabel}>Type</Text>
                  <Text style={styles.userInputValue}>{report.userInput.businessType}</Text>
                </View>
              )}
              {report.userInput.location && (
                <View style={styles.userInputItem}>
                  <Text style={styles.userInputLabel}>State</Text>
                  <Text style={styles.userInputValue}>{report.userInput.location}</Text>
                </View>
              )}
              {report.userInput.businessIndustry && (
                <View style={styles.userInputItem}>
                  <Text style={styles.userInputLabel}>Industry</Text>
                  <Text style={styles.userInputValue}>{report.userInput.businessIndustry}</Text>
                </View>
              )}
              {report.userInput.citiesOfOperation && (
                <View style={styles.userInputItem}>
                  <Text style={styles.userInputLabel}>Cities of Operation</Text>
                  <Text style={styles.userInputValue}>{report.userInput.citiesOfOperation}</Text>
                </View>
              )}
            </View>
          )}
        </View>
        {/* Sections */}
        {BUSINESS_COST_SECTIONS.map((section) => {
          const sectionContent = report.sections?.[section.id];
          if (!sectionContent) return null;
          const getSectionStyle = () => {
            if (section.isBubble) return styles.summarySection;
            return styles.section;
          };
          return (
            <View key={section.id} style={getSectionStyle()}>
              <View style={styles.sectionHeader} wrap={false}>
                <View style={styles.sectionTitleRow}>
                  {section.icon && (
                    <Image
                      src={`${ICON_BUSINESS_IDEAS_OVERVIEW_URL}${section.icon}`}
                      style={styles.sectionIcon}
                    />
                  )}
                  <Text style={styles.sectionTitle}>{formatSectionTitle(section.id)}</Text>
                </View>
                {sectionContent.cost && (
                  <Text style={getCostRangeStyle(sectionContent.cost)}>{sectionContent.cost}</Text>
                )}
              </View>

              <MarkdownPDFSection
                markdown={section.showDisclaimer ? DISCLAIMER : sectionContent.content}
              />
            </View>
          );
        })}

        <Link href={ROUTES.website(environment)} style={styles.footerImage} target="_blank">
          <Image
            src={`${CLOUDINARY_BASE_URL}/raster/business-ideas-banner.png`}
            style={styles.footerImage}
          />
        </Link>

        <View fixed style={styles.footer}>
          <View style={styles.footerRule} />

          <View style={styles.footerRow}>
            <Text style={styles.footerLeft}>support@zenbusiness.com</Text>
            <Image
              src={`${CLOUDINARY_BASE_URL}/raster/powered-by-zb-velo.png`}
              style={styles.footerRightImg}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
}

BusinessResultPDF.propTypes = {
  environment: PropTypes.string,
  report: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    userInput: PropTypes.object,
    sections: PropTypes.object
  }).isRequired
};

export default BusinessResultPDF;
